/**
 * Map Editor Right Properties Panel — edit selected photo/overlay properties
 */

import { useMapEditor } from '../MapEditorProvider';
import { DEFAULT_PHOTO_CATEGORIES } from '../../../../shared/types/project';

export function RightPropertiesPanel() {
  const { state, dispatch } = useMapEditor();
  const { selectedIds, selectedType, photos, floorPlans, customCategories } = state;

  if (selectedIds.length === 0) return null;

  const allCategories = [...DEFAULT_PHOTO_CATEGORIES, ...customCategories];

  // Multi-select batch editing
  if (selectedIds.length > 1 && selectedType === 'photo') {
    return (
      <div style={styles.panel}>
        <div style={styles.header}>
          <span style={styles.title}>{selectedIds.length} Photos Selected</span>
          <button onClick={() => dispatch({ type: 'CLEAR_SELECTION' })} style={styles.closeBtn}>&times;</button>
        </div>

        <div style={styles.body}>
          <label style={styles.label}>Change Category</label>
          <select
            onChange={(e) => {
              dispatch({ type: 'BATCH_UPDATE_PHOTOS', ids: selectedIds, updates: { categoryId: e.target.value } });
            }}
            style={styles.select}
            defaultValue=""
          >
            <option value="" disabled>Select category...</option>
            {allCategories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>

          <div style={{ borderTop: '1px solid #333', margin: '16px 0' }} />

          <button
            onClick={() => dispatch({ type: 'DELETE_PHOTOS', ids: selectedIds })}
            style={styles.deleteBtn}
          >
            Delete {selectedIds.length} Photos
          </button>
        </div>
      </div>
    );
  }

  // Single photo
  if (selectedType === 'photo') {
    const photo = photos.find(p => p.id === selectedIds[0]);
    if (!photo) return null;

    const update = (updates: Record<string, any>) => {
      dispatch({ type: 'UPDATE_PHOTO', id: photo.id, updates });
    };

    return (
      <div style={styles.panel}>
        <div style={styles.header}>
          <span style={styles.title}>Photo</span>
          <button onClick={() => dispatch({ type: 'CLEAR_SELECTION' })} style={styles.closeBtn}>&times;</button>
        </div>

        <div style={styles.body}>
          {photo.thumbnailPath && (
            <img src={photo.thumbnailPath} alt="" style={styles.thumbnail} />
          )}

          <label style={styles.label}>Name</label>
          <input
            type="text"
            value={photo.name || ''}
            onChange={(e) => update({ name: e.target.value })}
            style={styles.input}
            placeholder="Photo name"
          />

          <label style={styles.label}>Category</label>
          <select
            value={photo.categoryId}
            onChange={(e) => update({ categoryId: e.target.value })}
            style={styles.select}
          >
            {allCategories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>

          <label style={styles.label}>Description</label>
          <textarea
            value={photo.description || ''}
            onChange={(e) => update({ description: e.target.value })}
            style={{ ...styles.input, minHeight: 60, resize: 'vertical' as const }}
            placeholder="Description"
          />

          <label style={styles.label}>Type</label>
          <input
            type="text"
            value={photo.photoType || ''}
            onChange={(e) => update({ photoType: e.target.value })}
            style={styles.input}
            placeholder="e.g., Documentation, Inspection"
          />

          <label style={styles.label}>Area</label>
          <input
            type="text"
            value={photo.photoArea || ''}
            onChange={(e) => update({ photoArea: e.target.value })}
            style={styles.input}
            placeholder="e.g., Exterior, Interior"
          />

          <label style={styles.label}>Level</label>
          <input
            type="text"
            value={photo.level || ''}
            onChange={(e) => update({ level: e.target.value })}
            style={styles.input}
            placeholder="e.g., Ground/1st Floor"
          />

          <div style={{ borderTop: '1px solid #333', margin: '16px 0' }} />

          <label style={styles.label}>Coordinates</label>
          <div style={styles.coordRow}>
            <div style={styles.coordField}>
              <span style={styles.coordLabel}>Lng</span>
              <span style={styles.coordValue}>{photo.coordinates.longitude.toFixed(6)}</span>
            </div>
            <div style={styles.coordField}>
              <span style={styles.coordLabel}>Lat</span>
              <span style={styles.coordValue}>{photo.coordinates.latitude.toFixed(6)}</span>
            </div>
          </div>

          <div style={{ borderTop: '1px solid #333', margin: '16px 0' }} />

          <button
            onClick={() => dispatch({ type: 'DELETE_PHOTOS', ids: [photo.id] })}
            style={styles.deleteBtn}
          >
            Delete Photo
          </button>
        </div>
      </div>
    );
  }

  // Single overlay
  if (selectedType === 'overlay') {
    const overlay = floorPlans.find(fp => fp.id === selectedIds[0]);
    if (!overlay) return null;

    return (
      <div style={styles.panel}>
        <div style={styles.header}>
          <span style={styles.title}>Floor Plan</span>
          <button onClick={() => dispatch({ type: 'CLEAR_SELECTION' })} style={styles.closeBtn}>&times;</button>
        </div>

        <div style={styles.body}>
          <label style={styles.label}>Name</label>
          <input
            type="text"
            value={overlay.name}
            onChange={(e) => dispatch({ type: 'UPDATE_FLOOR_PLAN', id: overlay.id, updates: { name: e.target.value } })}
            style={styles.input}
          />

          <label style={styles.label}>Floor Number</label>
          <input
            type="number"
            value={overlay.floorNumber}
            onChange={(e) => dispatch({ type: 'UPDATE_FLOOR_PLAN', id: overlay.id, updates: { floorNumber: parseInt(e.target.value) || 0 } })}
            style={styles.input}
          />

          <label style={styles.label}>Opacity</label>
          <input
            type="range"
            min={0} max={1} step={0.05}
            value={overlay.opacity}
            onChange={(e) => dispatch({ type: 'UPDATE_FLOOR_PLAN', id: overlay.id, updates: { opacity: parseFloat(e.target.value) } })}
            style={{ width: '100%', accentColor: '#4F7CFF' }}
          />
          <div style={{ fontSize: 11, color: '#64748b', textAlign: 'right' as const }}>{Math.round(overlay.opacity * 100)}%</div>

          <div style={{ borderTop: '1px solid #333', margin: '16px 0' }} />

          <button
            onClick={() => { dispatch({ type: 'SET_ALIGNING_OVERLAY', id: overlay.id }); }}
            style={styles.alignBtn}
          >
            Align on Map
          </button>

          <button
            onClick={() => dispatch({ type: 'DELETE_FLOOR_PLAN', id: overlay.id })}
            style={styles.deleteBtn}
          >
            Delete Floor Plan
          </button>
        </div>
      </div>
    );
  }

  return null;
}

const styles: Record<string, React.CSSProperties> = {
  panel: {
    width: 280,
    background: 'rgba(0,0,0,0.75)',
    borderLeft: '1px solid #333',
    display: 'flex',
    flexDirection: 'column',
    flexShrink: 0,
    overflowY: 'auto',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 14px',
    borderBottom: '1px solid #333',
  },
  title: {
    fontSize: 13,
    fontWeight: 600,
    color: '#f1f5f9',
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    color: '#64748b',
    fontSize: 20,
    cursor: 'pointer',
    padding: '0 4px',
  },
  body: {
    padding: 14,
    flex: 1,
  },
  thumbnail: {
    width: '100%',
    height: 140,
    objectFit: 'cover' as const,
    borderRadius: 8,
    marginBottom: 14,
  },
  label: {
    display: 'block',
    fontSize: 11,
    fontWeight: 500,
    color: '#94a3b8',
    marginBottom: 4,
    marginTop: 12,
  },
  input: {
    width: '100%',
    padding: '8px 10px',
    background: '#0d0d0d',
    border: '1px solid #333',
    borderRadius: 6,
    color: '#f1f5f9',
    fontSize: 13,
    outline: 'none',
    boxSizing: 'border-box' as const,
  },
  select: {
    width: '100%',
    padding: '8px 10px',
    background: '#0d0d0d',
    border: '1px solid #333',
    borderRadius: 6,
    color: '#f1f5f9',
    fontSize: 13,
    outline: 'none',
  },
  coordRow: {
    display: 'flex',
    gap: 8,
  },
  coordField: {
    flex: 1,
    background: '#0d0d0d',
    border: '1px solid #282828',
    borderRadius: 6,
    padding: '6px 8px',
  },
  coordLabel: {
    fontSize: 10,
    color: '#64748b',
    display: 'block',
    marginBottom: 2,
  },
  coordValue: {
    fontSize: 12,
    fontFamily: 'monospace',
    color: '#94a3b8',
  },
  deleteBtn: {
    width: '100%',
    padding: '8px 12px',
    background: 'rgba(127,29,29,0.3)',
    border: '1px solid #7f1d1d',
    borderRadius: 6,
    color: '#fca5a5',
    fontSize: 12,
    fontWeight: 500,
    cursor: 'pointer',
    marginTop: 8,
  },
  alignBtn: {
    width: '100%',
    padding: '8px 12px',
    background: '#4F7CFF',
    border: 'none',
    borderRadius: 6,
    color: '#fff',
    fontSize: 12,
    fontWeight: 500,
    cursor: 'pointer',
  },
};
